import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileText, Image as ImageIcon, Upload, Trash2, Loader2, Download } from "lucide-react";
import { toast } from "sonner";

export function JobDocuments({ job, canEdit, onChanged }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const docs = job.documents || [];
  const fileUrl = (d) => `${api.defaults.baseURL}${(d.url || "").replace(/^\/api/, "")}`;

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setUploading(true);
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} exceeds 10MB`); continue; }
      try {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/jobs/${job.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`${file.name} attached`);
      } catch (err) { toast.error(err?.response?.data?.detail || `Upload failed: ${file.name}`); }
    }
    setUploading(false);
    onChanged();
  };

  return (
    <section data-testid="job-documents-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="sm-section-title">Documents</h2>
        {canEdit && (
          <>
            <input ref={fileRef} type="file" accept="application/pdf,image/*" multiple className="hidden" onChange={onFiles} data-testid="job-document-input" />
            <Button size="sm" className="sm-green" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="upload-document-button">
              {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}Upload
            </Button>
          </>
        )}
      </div>
      <div className="divide-y divide-slate-200 border-t border-slate-200">
        {job.doc_titles?.length > 0 && (
          <div className="py-2.5 text-sm" data-testid="documents-to-serve">
            <span className="sm-label mr-2">To be served:</span>
            {job.doc_titles.map((t) => <span key={t} className="inline-block mr-1.5 mb-1 rounded bg-slate-100 border border-slate-200 px-2 py-0.5 text-xs">{t}</span>)}
          </div>
        )}
        {docs.length === 0 ? <div className="py-4 text-sm text-slate-400">No files uploaded yet. Upload the summons, complaint, or other papers to serve.</div>
          : docs.map((d) => (
            <div key={d.id} className="py-2.5 flex items-center justify-between gap-2 text-sm" data-testid={`document-row-${d.id}`}>
              <a href={fileUrl(d)} target="_blank" rel="noreferrer" className="flex items-center gap-2 min-w-0 pl-1 group" data-testid={`document-link-${d.id}`} title="Open in new tab">
                {d.content_type === "application/pdf" ? <FileText className="h-4 w-4 text-rose-600 shrink-0" /> : <ImageIcon className="h-4 w-4 text-slate-500 shrink-0" />}
                <span className="sm-link font-medium truncate group-hover:underline">{d.name || d.filename}</span>
                <span className="text-xs text-slate-400 shrink-0">{d.size ? `${Math.max(1, Math.round(d.size / 1024))} KB` : ""}</span>
              </a>
              <div className="flex items-center gap-2 shrink-0">
                <a href={`${fileUrl(d)}?download=1`} className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 hover:border-[#2d7bd0] hover:text-[#2d7bd0]" data-testid={`download-document-${d.id}`} title="Download / print">
                  <Download className="h-3.5 w-3.5" /><span className="hidden sm:inline">Download</span>
                </a>
                {canEdit && (
                  <button type="button" className="text-slate-400 hover:text-rose-600 p-1" data-testid={`delete-document-${d.id}`} title="Remove" onClick={async () => {
                    if (!window.confirm("Remove this document?")) return;
                    await api.delete(`/jobs/${job.id}/documents/${d.id}`); toast.success("Removed"); onChanged();
                  }}><Trash2 className="h-3.5 w-3.5" /></button>
                )}
              </div>
            </div>
          ))}
      </div>
    </section>
  );
}
